import moment from 'moment';

export const REPORT_FILTER_STORAGE_KEY = 'managementReportsFiltersV1';

export const DATE_RANGE_OPTIONS = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 Days', value: 'last7days' },
  { label: 'Last 30 Days', value: 'last30days' },
  { label: 'Last 60 Days', value: 'last60days' },
  { label: 'Last 365 Days', value: 'last365days' },
  { label: 'Last Month', value: 'lastmonth' },
  { label: 'Last 12 Months', value: 'last12months' },
  { label: 'Last Year', value: 'lastyear' },
  { label: 'Week to Date', value: 'weektodate' },
  { label: 'Month to Date', value: 'monthtodate' },
  { label: 'Quarter to Date', value: 'quartertodate' },
  { label: 'Year to Date', value: 'yeartodate' },
  { label: 'Custom', value: 'custom' },
];

export const getReportDateRange = (selectedOption, now = moment()) => {
  let start = now.clone().startOf('day');
  let end = now.clone().endOf('day');

  switch (selectedOption) {
    case 'today':
      break;
    case 'yesterday':
      start = now.clone().subtract(1, 'day').startOf('day');
      end = now.clone().subtract(1, 'day').endOf('day');
      break;
    case 'last7days':
      start = now.clone().subtract(6, 'days').startOf('day');
      break;
    case 'last30days':
      start = now.clone().subtract(29, 'days').startOf('day');
      break;
    case 'last60days':
      start = now.clone().subtract(59, 'days').startOf('day');
      break;
    case 'last365days':
      start = now.clone().subtract(364, 'days').startOf('day');
      break;
    case 'lastmonth':
      start = now.clone().subtract(1, 'month').startOf('month');
      end = now.clone().subtract(1, 'month').endOf('month');
      break;
    case 'last12months':
      start = now.clone().subtract(12, 'months').startOf('month');
      end = now.clone().subtract(1, 'month').endOf('month');
      break;
    case 'lastyear':
      start = now.clone().subtract(1, 'year').startOf('year');
      end = now.clone().subtract(1, 'year').endOf('year');
      break;
    case 'weektodate':
      start = now.clone().startOf('week');
      break;
    case 'monthtodate':
      start = now.clone().startOf('month');
      break;
    case 'quartertodate':
      start = now.clone().startOf('quarter');
      break;
    case 'yeartodate':
      start = now.clone().startOf('year');
      break;
    case 'custom':
      return null;
    default:
      start = now.clone().subtract(6, 'days').startOf('day');
  }

  return {
    startDate: start.format('YYYY-MM-DD'),
    endDate: end.format('YYYY-MM-DD'),
  };
};

export const getPreviousPeriod = (startDate, endDate) => {
  const start = moment(startDate, 'YYYY-MM-DD', true);
  const end = moment(endDate, 'YYYY-MM-DD', true);
  if (!start.isValid() || !end.isValid() || start.isAfter(end)) return null;

  const rangeDays = end.diff(start, 'days') + 1;
  const previousEnd = start.clone().subtract(1, 'day');
  return {
    startDate: previousEnd.clone().subtract(rangeDays - 1, 'days').format('YYYY-MM-DD'),
    endDate: previousEnd.format('YYYY-MM-DD'),
  };
};

export const aggregateReportSummary = (rows = []) => {
  const totals = rows.reduce((result, row) => ({
    received: result.received + Number(row.receivedOrders || 0),
    dispatched: result.dispatched + Number(row.dispatchedOrders || 0),
    cancelled: result.cancelled + Number(row.cancelledOrders || 0),
  }), { received: 0, dispatched: 0, cancelled: 0 });

  const remaining = Math.max(totals.received - totals.dispatched - totals.cancelled, 0);
  const dispatchRate = totals.received > 0
    ? (totals.dispatched / totals.received) * 100
    : 0;

  return { ...totals, remaining, dispatchRate };
};

export const readReportWorkspaceState = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(REPORT_FILTER_STORAGE_KEY) || '{}');
    const validStart = moment(parsed.startDate, 'YYYY-MM-DD', true).isValid();
    const validEnd = moment(parsed.endDate, 'YYYY-MM-DD', true).isValid();
    const validRange = validStart && validEnd && !moment(parsed.startDate).isAfter(moment(parsed.endDate));
    const fallbackRange = getReportDateRange('last7days');

    return {
      activeReport: ['overall', 'client', 'courier', 'fulfillment'].includes(parsed.activeReport)
        ? parsed.activeReport
        : 'overall',
      granularity: ['Day', 'Week', 'Month'].includes(parsed.granularity)
        ? parsed.granularity
        : 'Day',
      dateRangeOption: DATE_RANGE_OPTIONS.some((option) => option.value === parsed.dateRangeOption)
        ? parsed.dateRangeOption
        : 'last7days',
      startDate: validRange ? parsed.startDate : fallbackRange.startDate,
      endDate: validRange ? parsed.endDate : fallbackRange.endDate,
      selectedClients: Array.isArray(parsed.selectedClients)
        ? parsed.selectedClients.filter((value) => typeof value === 'string' && value.trim())
        : [],
      comparePrevious: parsed.comparePrevious !== false,
    };
  } catch (error) {
    console.warn('Unable to restore report filters:', error);
    const fallbackRange = getReportDateRange('last7days');
    return {
      activeReport: 'overall',
      granularity: 'Day',
      dateRangeOption: 'last7days',
      startDate: fallbackRange.startDate,
      endDate: fallbackRange.endDate,
      selectedClients: [],
      comparePrevious: true,
    };
  }
};
